/**
 * "Set a budget" — a number and a rule. The spend is always computed from the
 * customer's invoices; nothing here is a running total someone has to keep.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { CATEGORY_COLOR, CATEGORY_LABEL, type BillingResponse, type BudgetRule, type WorkCategory } from "./model";

const THRESHOLDS = [50, 75, 90, 100];

function Toggle({
    on,
    onClick,
    children,
    color,
}: {
    on: boolean;
    onClick: () => void;
    children: React.ReactNode;
    color?: string;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={on}
            className={cn(
                "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[12.5px] transition-colors",
                on
                    ? "border-foreground/60 bg-foreground/5 font-semibold"
                    : "border-border text-muted-foreground hover:bg-muted/50",
            )}
        >
            {color && <span className="h-2.5 w-2.5 rounded-sm" style={{ background: color }} />}
            <span aria-hidden>{on ? "☑" : "☐"}</span>
            {children}
        </button>
    );
}

export function BudgetDialog({
    open,
    onClose,
    data,
    editing,
}: {
    open: boolean;
    onClose: () => void;
    data: BillingResponse;
    /** Present when editing an existing budget. */
    editing: (BudgetRule & { alertUserIds: string[] }) | null;
}) {
    const qc = useQueryClient();
    const thisYear = data.presets.find((p) => p.key === "this-year")!;
    const [name, setName] = useState(editing?.name ?? "");
    const [amount, setAmount] = useState(editing ? String(editing.amountCents / 100) : "");
    const [start, setStart] = useState(editing?.periodStart ?? thisYear.start);
    const [end, setEnd] = useState(editing?.periodEnd ?? thisYear.end);
    const [cats, setCats] = useState<WorkCategory[]>(editing?.categories ?? []);
    const [thresholds, setThresholds] = useState<number[]>(editing?.alertThresholds ?? [75, 90, 100]);
    const [people, setPeople] = useState<string[]>(editing?.alertUserIds ?? []);
    const [includePending, setIncludePending] = useState(editing?.alertIncludePending ?? true);
    const [notes, setNotes] = useState(editing?.notes ?? "");

    const toggle = <T,>(xs: T[], x: T) => (xs.includes(x) ? xs.filter((y) => y !== x) : [...xs, x]);
    const amountNum = Number(amount.replace(/[$,\s]/g, ""));
    const valid = name.trim() && amountNum > 0 && start && end && end >= start;

    const save = useMutation({
        mutationFn: () => {
            const body = {
                name: name.trim(),
                amount: amountNum,
                periodStart: start,
                periodEnd: end,
                categories: cats,
                alertThresholds: thresholds,
                alertUserIds: people,
                alertIncludePending: includePending,
                notes: notes.trim() || null,
            };
            return editing
                ? api.patch(`/api/customer-budgets/${editing.id}`, body)
                : api.post(`/api/customers/${data.customer.id}/budgets`, body);
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["customer-billing", data.customer.id] });
            toast.success(editing ? "Budget updated." : "Budget saved — it keeps count from here.");
            onClose();
        },
        onError: (err) => {
            const info = err instanceof ApiError ? (err.body as { message?: string } | null) : null;
            toast.error(info?.message ?? "Couldn't save the budget.");
        },
    });

    const presetFor = (key: string) => {
        const p = data.presets.find((x) => x.key === key);
        if (p) {
            setStart(p.start);
            setEnd(p.end);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={
                editing ? `Edit budget — ${editing.name}` : `New budget for ${data.customer.name ?? "this customer"}`
            }
        >
            <div className="space-y-4 text-sm">
                <div className="grid gap-3 sm:grid-cols-[1fr_180px]">
                    <label className="block">
                        <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                            Name
                        </span>
                        <Input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Repairs & replacements"
                        />
                    </label>
                    <label className="block">
                        <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                            Amount
                        </span>
                        <Input
                            inputMode="decimal"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            placeholder="$215,000"
                        />
                    </label>
                </div>

                <div>
                    <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                        Period
                    </span>
                    <div className="flex flex-wrap items-center gap-2">
                        {data.presets
                            .filter((p) => p.key !== "this-month")
                            .map((p) => (
                                <Toggle
                                    key={p.key}
                                    on={start === p.start && end === p.end}
                                    onClick={() => presetFor(p.key)}
                                >
                                    {p.label}
                                </Toggle>
                            ))}
                        <Input
                            type="date"
                            value={start}
                            onChange={(e) => setStart(e.target.value)}
                            className="h-8 w-[150px]"
                            aria-label="Starts"
                        />
                        <span className="text-muted-foreground">to</span>
                        <Input
                            type="date"
                            value={end}
                            onChange={(e) => setEnd(e.target.value)}
                            className="h-8 w-[150px]"
                            aria-label="Ends"
                        />
                    </div>
                </div>

                <div>
                    <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                        What counts against it
                    </span>
                    <div className="flex flex-wrap gap-2">
                        {(Object.keys(CATEGORY_LABEL) as WorkCategory[]).map((c) => (
                            <Toggle
                                key={c}
                                on={cats.includes(c)}
                                onClick={() => setCats(toggle(cats, c))}
                                color={CATEGORY_COLOR[c]}
                            >
                                {CATEGORY_LABEL[c]}
                            </Toggle>
                        ))}
                    </div>
                    <p className="mt-1 text-[12px] text-muted-foreground">
                        {cats.length === 0
                            ? "Nothing ticked = everything billed to this customer."
                            : "Sorted by each invoice's job type (Backflow Test, Backflow Repair…, Backflow Replacement…)."}
                    </p>
                </div>

                <div>
                    <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                        Heads-up when it reaches
                    </span>
                    <div className="flex flex-wrap items-center gap-2">
                        {THRESHOLDS.map((t) => (
                            <Toggle
                                key={t}
                                on={thresholds.includes(t)}
                                onClick={() => setThresholds(toggle(thresholds, t))}
                            >
                                {t}%
                            </Toggle>
                        ))}
                        <Toggle on={includePending} onClick={() => setIncludePending(!includePending)}>
                            Count quotes waiting on approval
                        </Toggle>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                        {data.alertPeople.map((p) => (
                            <Toggle
                                key={p.id}
                                on={people.includes(p.id)}
                                onClick={() => setPeople(toggle(people, p.id))}
                            >
                                {p.name}
                            </Toggle>
                        ))}
                    </div>
                    <p className="mt-1 text-[12px] text-muted-foreground">
                        {people.length === 0
                            ? "Nobody picked = a task in the Office queue."
                            : "Each person gets a task and a bell, once per line. Never the customer."}
                    </p>
                </div>

                <label className="block">
                    <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                        Where the number comes from (optional)
                    </span>
                    <Input
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="2026–27 Scope of Work — “will not exceed $215,000”"
                    />
                </label>

                <div className="flex justify-end gap-2 border-t pt-3">
                    <Button variant="outline" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button disabled={!valid || save.isPending} onClick={() => save.mutate()}>
                        {editing ? "Save changes" : "Save budget"}
                    </Button>
                </div>
            </div>
        </Dialog>
    );
}
